import { useCallback, useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import Icon from '../Icon'
import IconButton from '../IconButton'
import styles from './CopyPageLink.module.scss'

const CONFIRMATION_MS = 1500

/**
 * Copies the current page's URL.
 *
 * Atlas runs as a standalone PWA, so there is no address bar to read a link out
 * of — without this there is no way to share the page you are on. Best-effort by
 * design: a browser that blocks the clipboard leaves the tooltip on its idle
 * label rather than raising at the user.
 */
const CopyPageLink = () => {
  const [isCopied, setIsCopied] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => () => clearTimeout(timeoutRef.current), [])

  const handleCopy = useCallback(() => {
    const confirm = () => {
      setIsCopied(true)
      clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => setIsCopied(false), CONFIRMATION_MS)
    }

    try {
      void navigator.clipboard.writeText(window.location.href).then(confirm, () => {})
    } catch {
      // Clipboard unavailable (insecure context, permission denied) — nothing to do.
    }
  }, [])

  const label = isCopied ? 'Link copied' : 'Copy link to this page'

  return (
    <IconButton
      title={label}
      className={cn(styles.trigger, { [styles.copied]: isCopied })}
      size="small"
      data-cy="copy-page-link-button"
      onClick={handleCopy}
    >
      {/* Swapped rather than tooltip-only, matching Common/CopyToClipboard: the tooltip
          is gone the moment the pointer leaves, so the tick is the only confirmation a
          user who clicks and looks away actually sees. */}
      {isCopied ? <Icon name="check" size="sm" /> : <Icon name="link" size="sm" />}
    </IconButton>
  )
}

export default CopyPageLink
